import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

function formatDate(raw) {
  if (!raw) return "";
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function SplitListPreview({
  label = "Liste",
  command = "ls entries/",
  items = [],
  loading = false,
  error = null,
  introKicker = "Editorial",
  introHeadline = "Feed",
  mobileTabs = null,
  attachTop = false,
  hideCommandOnMobile = false,
  hideCommand = false,
  showDesktopPreview = true,
}) {
  const [activeId, setActiveId] = useState("");
  const [expandedId, setExpandedId] = useState("");

  useEffect(() => {
    if (!items.length) {
      setActiveId("");
      setExpandedId("");
      return;
    }
    if (!items.find((item) => item.id === activeId)) {
      setActiveId(items[0].id);
    }
    if (!items.find((item) => item.id === expandedId)) {
      setExpandedId("");
    }
  }, [items, activeId, expandedId]);

  const handleItemClick = (id) => {
    setActiveId(id);
    setExpandedId((prev) => (prev === id ? "" : id));
  };

  const active = useMemo(
    () => items.find((item) => item.id === activeId) || items[0],
    [items, activeId]
  );

  return (
    <div className="max-w-[1120px] mx-auto px-0 sm:px-2">
      <p
        className={`text-xs text-center text-[#EFEBDC]/50 mb-6 font-mono ${
          hideCommand ? "hidden" : hideCommandOnMobile ? "hidden lg:block" : ""
        }`}
      >
        <span className="text-[#AC8E66]">$</span> {command}
        <span className={`${error ? "text-red-400/80" : "text-green-400/70"} ml-4`}>
          &gt;{" "}
          {error
            ? "fehler beim laden."
            : loading
              ? "laden..."
              : `${items.length} ${label.toLowerCase()} gefunden.`}
        </span>
      </p>

      {mobileTabs && (
        <div className="lg:hidden mb-0">
          {mobileTabs}
        </div>
      )}

      <section
        className={`overflow-hidden border border-[#2B2B2B] backdrop-blur-[1px] ${
          mobileTabs || attachTop
            ? "rounded-b-2xl rounded-t-none lg:rounded-2xl"
            : "rounded-2xl"
        }`}
        style={{
          "--split-paper": "#dbd8cc",
          "--split-ink": "#111111",
          "--split-muted": "#8B8A84",
          "--split-accent": "#AC8E66",
        }}
      >
        <div
          className={`grid grid-cols-1 ${
            showDesktopPreview ? "lg:grid-cols-[minmax(340px,40%)_1fr]" : ""
          } min-h-[64vh]`}
        >
          <aside
            className={`relative p-6 sm:p-8 ${
              showDesktopPreview
                ? "lg:self-start lg:sticky lg:top-28 lg:h-[calc(100vh-12rem)] lg:min-h-[460px] lg:max-h-[640px] lg:overflow-y-auto"
                : ""
            }`}
            style={{ background: "var(--split-paper)", color: "var(--split-ink)" }}
          >
            <div className="relative z-10 mb-8">
              <p
                className="uppercase tracking-[0.18em] text-[10px] font-mono"
                style={{ color: "var(--split-muted)" }}
              >
                {introKicker}
              </p>
              <p className="text-[20px] sm:text-[22px] font-black leading-tight mt-3">
                {introHeadline}
              </p>
            </div>

            <div className="relative z-10 space-y-3">
              {loading &&
                Array.from({ length: 5 }).map((_, idx) => (
                  <div key={idx} className="h-16 rounded-lg bg-black/5 animate-pulse" />
                ))}

              {!loading &&
                !error &&
                items.map((item, idx) => {
                  const isExpanded = expandedId === item.id;
                  return (
                    <div key={item.id || idx}>
                      <button
                        type="button"
                        onMouseEnter={() => setActiveId(item.id)}
                        onFocus={() => setActiveId(item.id)}
                        onClick={() => handleItemClick(item.id)}
                        className="group w-full text-left"
                      >
                        <div
                          className={`-ml-6 sm:-ml-10 pl-6 sm:pl-10 pr-3 sm:pr-4 py-3 sm:py-4 rounded-r-xl 
                            border-l-2 transition-all duration-200 ${
                            activeId && activeId !== item.id
                              ? "opacity-70 blur-[1px]"
                              : "opacity-100 blur-0"
                          } ${activeId === item.id ? "backdrop-blur-[4px]" : ""}`}
                          style={{
                            background:
                              activeId === item.id ? "rgba(219,216,204,0.45)" : "rgba(17,17,17,0.0)",
                            borderLeftColor:
                              activeId === item.id
                                ? "var(--split-accent)"
                                : "rgba(17,17,17,0)",
                          }}
                        >
                          <p
                            className="text-[11px] uppercase tracking-wide font-semibold"
                            style={{ color: "var(--split-accent)" }}
                          >
                            {(item.meta || "Eintrag").toUpperCase()}
                            {item.date ? ` • ${formatDate(item.date)}` : ""}
                          </p>
                          <p className="text-[11px] sm:text-[14px] font-extrabold leading-snug mt-1 line-clamp-2">
                            {item.title || "Ohne Titel"}
                          </p>
                        </div>
                      </button>

                      <div
                        className={`overflow-hidden transition-all duration-300 ${
                          isExpanded ? "max-h-64 opacity-100 mt-2" : "max-h-0 opacity-0"
                        }`}
                      >
                        <div className="ml-1 rounded-lg border border-black/10 bg-[#d8d4c6] overflow-hidden">
                          <div className="p-3">
                            {item.subtitle && (
                              <p className="text-[12px] sm:text-[13px] leading-relaxed text-[#1a1a1a]/80 line-clamp-3">
                                {item.subtitle}
                              </p>
                            )}
                            {item.href &&
                              (item.external ? (
                                <a
                                  href={item.href}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-block mt-2 text-[12px] font-semibold underline underline-offset-4 text-[#1a1a1a]"
                                >
                                  <span className="text-brand/70">&gt; </span> Artikel lesen
                                </a>
                              ) : (
                                <Link
                                  to={item.href}
                                  className="inline-block mt-2 text-[12px] font-semibold underline underline-offset-4 text-[#1a1a1a]"
                                >
                                  <span className="text-brand/70">&gt; </span> Artikel lesen
                                </Link>
                              ))}
                          </div>
                          {item.image && (
                            <div
                              className={`bg-[#d0cbb8] border-t border-black/10 ${
                                showDesktopPreview ? "lg:hidden" : ""
                              }`}
                            >
                              <img
                                src={item.image}
                                alt={item.title || "Vorschaubild"}
                                loading="lazy"
                                className="w-full h-28 sm:h-36 object-cover object-center"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          </aside>

          {showDesktopPreview && (
            <div
              className="hidden lg:grid grid-rows-[1fr_1fr] self-start sticky top-28 h-[calc(100vh-12rem)] min-h-[460px] max-h-[640px]"
              style={{
                background: "linear-gradient(180deg, rgba(255,255,255,0.06), rgba(17,17,17,0.10)), #d0cbb8",
              }}
            >
              <div className="relative min-h-0 overflow-hidden bg-[#1a1a1a]">
                {active?.image ? (
                  <img
                    src={active.image}
                    alt={active.title || "Featured Bild"}
                    className="absolute inset-0 w-full h-128 object-cover object-top"
                    style={{ filter: "grayscale(0.3) contrast(0.9)" }}
                    loading="lazy"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center p-8 text-center text-[#151515]/60 font-mono">
                    Keine Bildvorschau vorhanden
                  </div>
                )}

                <div className="absolute inset-0 bg-black/0" />
                <div
                  className="pointer-events-none absolute inset-0"
                  style={{
                    backgroundImage: `radial-gradient(circle at 1px 1px, rgba(17,17,17,0.15) 5px, transparent 6.25px),
                      linear-gradient(180deg, rgba(255,255,255,0.08), rgba(17,17,17,0.08))`,
                    backgroundSize: "30px 30px, 100% 100%",
                    opacity: 0.28,
                  }}
                />
              </div>

              <div
                className="p-4 sm:p-6 backdrop-blur-md border-t border-white/5"
                style={{ background: "#1a1a1a" }}
              >
                <div
                  className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] uppercase tracking-widest"
                  style={{ background: "rgba(17,17,17,0.7)", color: "#F6F4EE" }}
                >
                  Featured
                </div>
                <h3 className="mt-3 text-xl sm:text-3xl font-black text-[#F6F4EE] leading-snug max-w-2xl">
                  {active?.title || "Auswahl"}
                </h3>
                <p className="mt-2 text-xs sm:text-sm text-[#F6F4EE]/85 max-w-xl line-clamp-2">
                  {active?.subtitle || active?.meta || "Vorschau"}
                </p>
                {active?.href &&
                  (active.external ? (
                    <a
                      href={active.href}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-block mt-4 text-sm font-semibold text-[#F6F4EE] underline underline-offset-4"
                    >
                      <span className="text-brand/70">&gt; </span> Artikel lesen
                    </a>
                  ) : (
                    <Link
                      to={active.href}
                      className="inline-block mt-4 text-sm font-normal text-[#F6F4EE] underline underline-offset-4"
                    >
                      <span className="text-brand/70">&gt; </span> Artikel lesen
                    </Link>
                  ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
